import type { WorkflowDefinition } from "../types";

/**
 * W17 — Measure AI as a marketing channel.
 *
 * `shared` and `observed`: this is standing setup on the property. A site that
 * already has the plugin installed and Google Analytics connected really is
 * most of the way through it, whoever did the work.
 */
export const aiChannelWorkflow: WorkflowDefinition = {
	id: "ai-channel",
	wNumber: 17,
	title: "Measure AI as a marketing channel",
	outcome:
		"Give AI a row in your reporting, beside paid, organic, email and social.",
	audiences: ["inhouse"],
	uses: [
		"Analytics Setup",
		"AI Traffic Analytics",
		"Google Analytics",
		"Recrawl Frequency",
	],
	scope: "property",
	concurrency: "shared",
	progress: "observed",
	// Bot detection needs a site of the subject's own to put the plugin on.
	isApplicable: (mode) => mode.showAnalytics,
	articleSlug: "how-to-measure-ai-as-a-marketing-channel",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "install-plugin",
			title: "Install the plugin so AI visits get counted",
			page: "Analytics Setup",
			route: "/analytics",
			criterion: "You've installed the plugin and traffic is arriving.",
			why: "AI assistants do not execute JavaScript, so a browser tag never sees them. Detection has to sit on the server.",
			docsHref: "/docs/dashboards/ai-traffic-analytics",
			tasks: [
				"Install the plugin for your platform",
				"Load a page, then wait for the first traffic to arrive",
			],
			complete: { kind: "flag", key: "isVerified" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "checklist",
							items: [
								{
									label: "Traffic arriving",
									done: state.property.isVerified,
								},
							],
						}
					: null,
		},
		{
			id: "two-numbers",
			title: "Read bot visits against AI-referred visits",
			page: "AI Traffic Analytics",
			route: "/ai-analytics",
			criterion: "You've read both numbers.",
			why: "Bot visits say AI used your content. Referred visits say a person clicked through. They answer different questions.",
			docsHref: "/docs/dashboards/ai-traffic-analytics",
			tasks: [
				"Read the two counts separately, never as one total",
				"Expect referred visits to be a small share of bot visits",
				"Check which pages AI reaches for most often",
			],
			// Reading two numbers and drawing a conclusion is a judgement call.
			complete: { kind: "manual" },
		},
		{
			id: "forward-to-ga",
			title: "Send AI visits to Google Analytics",
			page: "Google Analytics",
			route: "/analytics",
			criterion: "You've connected Google Analytics.",
			why: "AI belongs in the report your team already reads. A number in a tool nobody opens changes nothing.",
			docsHref: "/docs/dashboards/google-analytics",
			tasks: [
				"Add your measurement ID and API secret",
				"Build a custom channel group for AI traffic",
				"Check the events are arriving in Google Analytics",
			],
			// The platform string an AnalyticsIntegration row stores.
			complete: {
				kind: "analyticsPlatform",
				platform: "google-analytics",
			},
			signal: ({ state }) =>
				state.property
					? {
							kind: "checklist",
							items: [
								{
									label: "Google Analytics connected",
									done: state.property.analyticsPlatforms.includes(
										"google-analytics",
									),
								},
							],
						}
					: null,
		},
		{
			id: "time-publishing",
			title: "Time publishing to when AI comes back",
			page: "Recrawl Frequency",
			route: "/recrawl",
			criterion: "You've checked how often crawlers return.",
			why: "Judging a change the next morning is judging it before AI has read the page. The cadence tells you when to look.",
			docsHref: "/docs/dashboards/recrawl-frequency",
			tasks: [
				"Read the site-wide average interval",
				"Line the publishing and reporting calendar up with it",
			],
			complete: { kind: "manual" },
		},
	],
};
